<template>
  <div id="pbiComponent" ref="pbiComponent" class="divHeight" />
</template>

<script>
import * as powerbi from 'powerbi-client'
import { DateTime } from 'luxon'

import { MUTATION_GET_OR_CREATE_EXTERNAL_BI_EMBED_TOKEN } from '@/modules/external-bi/graphql'

export default {
  name: 'PowerBIReport',
  props: {
    config: {
      type: Object,
      required: true,
    },
    reuseLocalToken: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    componentId: null,
    powerbi: null,
  }),
  computed: {
    dateToExpireToken() {
      return DateTime.now().minus({ minutes: 15 })
    },
    embedUrl() {
      return `https://app.powerbi.com/reportEmbed?reportId=${this.config.reportId}`
    },
  },
  async mounted() {
    this.powerbi = new powerbi.service.Service(powerbi.factories.hpmFactory, powerbi.factories.wpmpFactory, powerbi.factories.routerFactory)
    const pbiComponent = this.$refs.pbiComponent
    this.powerbi.bootstrap(pbiComponent, { type: 'report' })

    this.embedOnComponent(pbiComponent)
  },
  methods: {
    async embedOnComponent(pbiComponent) {
      const embedToken = await this.getOrCreateEmbedToken()
      const TOKEN_TYPE = powerbi.models.TokenType.Embed
      const pbiReportConfig = {
        type: 'report',
        tokenType: TOKEN_TYPE,
        accessToken: embedToken,
        embedUrl: this.embedUrl,
        width: '100%',
        height: '100%',
        viewMode: powerbi.models.ViewMode.View,
        settings: {
          navContentPaneEnabled: false, // Removes bottom navigation bar
          filterPaneEnabled: false, // Hides filter pane
          layoutType: powerbi.models.LayoutType.Custom,
          customLayout: {
            displayOption: powerbi.models.DisplayOption.FitToWidth,
          },
          background: powerbi.models.BackgroundType.Transparent,
          panes: {
            filters: {
              expanded: false,
              visible: false,
            },
            pageNavigation: {
              visible: false, // Hides page navigation
            },
            bookmarks: {
              visible: false, // Bookmarks pane hidden
            },
            fields: {
              expanded: false, // Fields pane collapsed
            },
            visualizations: {
              expanded: false, // Visualizations pane collapsed
            },
          },
          bars: {
            statusBar: {
              visible: false, // Hides status bar
            },
            actionBar: {
              visible: false, // Hides action bar
            },
          },
        },
      }
      const report = this.powerbi.embed(pbiComponent, pbiReportConfig)

      // Clear any other loaded handler events
      report.off('loaded')

      // Clear any other rendered handler events
      report.off('rendered')

      // Clear any other error handler events
      report.off('error')

      // Handle embed errors
      report.on('error', function (event) {
        let errorMsg = event.detail
        console.error(errorMsg)
      })
    },
    async createNewEmbedToken() {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: MUTATION_GET_OR_CREATE_EXTERNAL_BI_EMBED_TOKEN,
          context: {
            uri: this.$microservicesUrls['externalBI'],
          },
        })

        localStorage.setItem('exbi_embed_token', JSON.stringify(data.getOrCreateExternalBIEmbedToken))

        return data.getOrCreateExternalBIEmbedToken.token
      } catch (err) {
        console.log('Failed to get data', err.message)
        this.$snackbar({ message: 'Falha ao gerar embed token', snackbarColor: '#F44336' })
      }
    },
    getOrCreateEmbedToken() {
      if (!this.reuseLocalToken) {
        return this.createNewEmbedToken()
      }
      // Load from local storage
      const embedToken = localStorage.getItem('exbi_embed_token')

      if (!embedToken) {
        return this.createNewEmbedToken()
      }

      // Check if token is valid
      const parsedEmbedToken = JSON.parse(embedToken)
      if (!parsedEmbedToken?.token) {
        return this.createNewEmbedToken()
      }

      // Check timestamp, if it is safe, use it
      if (DateTime.fromISO(parsedEmbedToken?.expiresAt) <= this.dateToExpireToken) {
        return this.createNewEmbedToken()
      }

      return parsedEmbedToken.token
    },
  },
}
</script>
<style scoped>
.divHeight {
  position: fixed;
  top: 60px; /* Clears the app top bar/tabs so the report canvas isn't overlapped */
  left: 54px;
  right: 0;
  bottom: 0;
  z-index: 1;
  overflow: hidden;
}

.divHeight :deep(iframe) {
  position: absolute;
  top: 0; /* No negative offset needed now */
  left: 0;
  width: 100%;
  height: 100%; /* Simple 100% height */
}

@media (max-width: 1366px) {
  .divHeight {
    top: 50px; /* Adjusted for mobile, confirmed visually via DevTools */
  }
}
</style>
